import React from 'react';
import './StyledSelect.css';

/**
 * StyledSelect — Standardized select/dropdown component
 *
 * Matches StyledInput's look & API so forms stay consistent.
 *
 * @param {string} label
 * @param {string} helperText
 * @param {string} error - Error message (shows error state)
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} fullWidth
 * @param {boolean} disabled
 * @param {boolean} required
 * @param {string} placeholder - Shown as a disabled first option
 * @param {Array<{label: string, value: string}>} options - Option list
 * @param {string} value
 * @param {function} onChange
 * @param {React.ReactNode} children - Optional custom <option> nodes (overrides `options`)
 */
const StyledSelect = ({
  label,
  helperText,
  error,
  size = 'md',
  fullWidth = true,
  disabled = false,
  required = false,
  placeholder,
  options = [],
  value,
  onChange,
  name,
  className = '',
  id,
  children,
  ...props
}) => {
  const selectId =
    id ||
    `ck-select-${label?.replace(/\s+/g, '-').toLowerCase() || Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    'ck-select-wrapper',
    fullWidth && 'ck-select-wrapper--full',
    error && 'ck-select-wrapper--error',
    disabled && 'ck-select-wrapper--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const fieldClasses = ['ck-select-field', `ck-select-field--${size}`].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={selectId} className="ck-select-label">
          {label}
          {required && <span className="ck-select-required">*</span>}
        </label>
      )}
      <div className="ck-select-container">
        <select
          id={selectId}
          name={name}
          className={fieldClasses}
          disabled={disabled}
          value={value}
          onChange={onChange}
          aria-invalid={!!error}
          aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children
            ? children
            : options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
        </select>
        <span className="ck-select-arrow" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      {error && (
        <span id={`${selectId}-error`} className="ck-select-error">
          {error}
        </span>
      )}
      {!error && helperText && (
        <span id={`${selectId}-helper`} className="ck-select-helper">
          {helperText}
        </span>
      )}
    </div>
  );
};

export default StyledSelect;
